import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

// Cada cuánto se consulta el backend. La ventana solo aparece cuando,
// además, ya pasó el intervalo configurado por el administrador.
const POLL_INTERVAL_MS = 60000;

// Evita ventanas duplicadas entre pestañas del mismo navegador que
// consultan casi al mismo tiempo.
const PROMPT_GUARD_MS = 30000;

const lastAcceptedKey = userId => `oldTicketsReminder_lastAcceptedAt_${userId}`;
const lastPromptedKey = userId => `oldTicketsReminder_lastPromptedAt_${userId}`;

const OldTicketsReminderModal = () => {
  const { user } = useContext(AuthContext);
  const [reminder, setReminder] = useState(null);
  const [open, setOpen] = useState(false);
  const checkingRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return undefined;

    const checkReminder = async () => {
      if (checkingRef.current) return;
      checkingRef.current = true;

      try {
        const { data } = await api.get("/api/old-tickets-reminder");

        if (!data.enabled || !data.count) {
          checkingRef.current = false;
          return;
        }

        const now = Date.now();
        const intervalMs = data.intervalMinutes * 60000;

        const lastAccepted = Number(
          localStorage.getItem(lastAcceptedKey(user.id)) || 0
        );
        const lastPrompted = Number(
          localStorage.getItem(lastPromptedKey(user.id)) || 0
        );

        const dueByAcceptance = now - lastAccepted >= intervalMs;
        const notRecentlyPrompted = now - lastPrompted >= PROMPT_GUARD_MS;

        if (dueByAcceptance && notRecentlyPrompted) {
          localStorage.setItem(lastPromptedKey(user.id), String(now));
          setReminder(data);
          setOpen(true);
        }
      } catch (err) {
        // Falla silenciosa: es un aviso informativo, no debe interrumpir
        // el trabajo del operario ni mostrar errores por conexión.
      } finally {
        checkingRef.current = false;
      }
    };

    checkReminder();
    const interval = setInterval(checkReminder, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [user?.id]);

  const handleAccept = () => {
    if (user?.id) {
      localStorage.setItem(lastAcceptedKey(user.id), String(Date.now()));
    }
    setOpen(false);
  };

  if (!reminder) return null;

  return (
    <Dialog open={open} onClose={handleAccept} maxWidth="xs" fullWidth>
      <DialogTitle>{i18n.t("oldTicketsReminder.title")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {i18n.t("oldTicketsReminder.message", {
            count: reminder.count,
            days: reminder.thresholdDays,
          })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAccept} color="primary" variant="contained">
          {i18n.t("oldTicketsReminder.accept")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OldTicketsReminderModal;
